import { CloseOutlineIcon, InfoIcon } from '@carboncopyco/icons';
import classNames from 'classnames';
import type { Id as ToastId, ToastOptions } from 'react-toastify';
import { toast } from 'react-toastify';
import { TextCaption } from '../Typography';
import { bgColor, closeIconColor, infoIconColor, textColor } from './colors';

const toastTypes = ['info', 'success', 'error', 'dark', 'warning'] as const;

type ToastType = (typeof toastTypes)[number];
type ToastContent = string | JSX.Element;
type NotifyCallback = (
  content?: ToastContent,
  params?: ToastOptions
) => ToastId;

export type NotifyCallbacks = {
  [key in ToastType]: NotifyCallback;
};

const defaultProps = (type: ToastType): ToastOptions => {
  return {
    closeButton: () => (
      <div className="flex items-center">
        <CloseOutlineIcon
          color={closeIconColor[type]}
          size={16}
        />
      </div>
    ),
    icon: () => (
      <div className="flex items-center">
        <InfoIcon
          color={infoIconColor[type]}
          size={24}
        />
      </div>
    ),
    className: () =>
      classNames(
        'flex items-center leading-none mb-2.5 px-4 py-2.5 min-h-10 justify-between overflow-hidden cursor-pointer rounded-md shadow-ds-sm',
        bgColor[type]
      ),
    bodyClassName: () => 'flex items-center gap-1',
  };
};

const showToast = (type: ToastType, content: ToastContent = '', params = {}) =>
  typeof content === 'string'
    ? toast[type](
        <TextCaption
          label={content}
          color={textColor[type]}
        />,
        {
          ...params,
          data: { content },
          pauseOnFocusLoss: false,
          ...defaultProps(type),
        }
      )
    : toast[type](content, {
        ...params,
        data: { content },
        pauseOnFocusLoss: false,
        ...defaultProps(type),
      });

export const notifyCallbacks = toastTypes.reduce<NotifyCallbacks>(
  (callbacks, type) =>
    Object.assign(callbacks, {
      [type]: (...args) => showToast(type, ...args),
    }),
  {} as NotifyCallbacks
);

export type { ToastType, ToastContent, ToastOptions, ToastId };
